import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/** ISO2 lookup used only for flag rendering. Falls back to 🌍 if unknown. */
const COUNTRY_ISO = {
  Algeria: "DZ", Angola: "AO", Benin: "BJ", Botswana: "BW", "Burkina Faso": "BF", Burundi: "BI",
  Cameroon: "CM", "Cape Verde": "CV", "Central African Republic": "CF", Chad: "TD", Comoros: "KM",
  Congo: "CG", "DR Congo": "CD", Djibouti: "DJ", Egypt: "EG", "Equatorial Guinea": "GQ",
  Eritrea: "ER", Eswatini: "SZ", Ethiopia: "ET", Gabon: "GA", Gambia: "GM", Ghana: "GH",
  Guinea: "GN", "Guinea-Bissau": "GW", "Ivory Coast": "CI", Kenya: "KE", Lesotho: "LS",
  Liberia: "LR", Libya: "LY", Madagascar: "MG", Malawi: "MW", Mali: "ML", Mauritania: "MR",
  Mauritius: "MU", Morocco: "MA", Mozambique: "MZ", Namibia: "NA", Niger: "NE", Nigeria: "NG",
  Rwanda: "RW", "Sao Tome and Principe": "ST", Senegal: "SN", Seychelles: "SC",
  "Sierra Leone": "SL", Somalia: "SO", "South Africa": "ZA", "South Sudan": "SS", Sudan: "SD",
  Tanzania: "TZ", Togo: "TG", Tunisia: "TN", Uganda: "UG", Zambia: "ZM", Zimbabwe: "ZW",
};

function flag(name) {
  const iso2 = COUNTRY_ISO[name];
  if (!iso2) return "🌍";
  return String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
}

export default function CountrySpotlights() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/spotlights?per_country=6")
      .then((r) => setData(r.data || []))
      .catch((err) => console.debug("spotlights load failed", err?.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading || data.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="country-spotlights-section">
      <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("spotlights.overline")}</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
            {t("spotlights.titleA")} <span className="text-[#eab308]">{t("spotlights.titleB")}</span>.
          </h2>
        </div>
        <p className="md:col-span-4 text-white/70">
          {t("spotlights.subtitle")}
        </p>
      </div>

      <div className="space-y-14">
        {data.map((group) => (
          <div key={group.country} data-testid={`spotlight-${group.country}`}>
            {/* Country header */}
            <div className="flex items-center justify-between mb-5 border-b border-white/10 pb-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl leading-none" aria-hidden>{flag(group.country)}</span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{t("spotlights.verifiedFrom")}</div>
                  <div className="font-display text-2xl md:text-3xl uppercase font-bold tracking-tight">{group.country}</div>
                </div>
              </div>
              <Link
                to={`/country/${encodeURIComponent(group.country)}`}
                data-testid={`spotlight-view-all-${group.country}`}
                className="text-xs uppercase tracking-widest text-[#e65f2b] hover:text-[#eab308] flex items-center gap-1"
              >
                {t("spotlights.seeAll")} <ArrowRight size={12} />
              </Link>
            </div>

            {/* Players grid (6 per country) */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {group.players.map((p) => (
                <Link
                  key={p.user_id}
                  to={`/players/${p.user_id}`}
                  data-testid={`spotlight-card-${p.user_id}`}
                  className="group border border-white/10 bg-[#141414] hover:border-[#eab308] transition-colors"
                >
                  <div className="aspect-[3/4] bg-black relative overflow-hidden">
                    {p.photo_path
                      ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center font-display text-5xl text-white/20">{(p.name || "?")[0]}</div>}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <div className="absolute top-2 right-2 bg-[#eab308] text-black w-6 h-6 rounded-full flex items-center justify-center shadow-lg">
                      <ShieldCheck size={12} strokeWidth={3} />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-display text-sm font-bold uppercase truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1 truncate">
                      {p.position || "—"} · {p.age ? `${p.age}y` : "—"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
